import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox, Input, message, Select } from 'antd'
import styled from 'styled-components/macro'
import {
  RECIPE_SUPPLIER_TITLE,
  RECIPE_TYPE,
  buildDefaultRecipeSku,
  getRecipeIdString,
  getRecipePriceForMode,
} from 'src/utils/stockTakeRecipeProduct'
import {
  buildEmptyAddProductFields,
  getAddProductFocusField,
  ADD_PRODUCT_FOCUS_FIELD,
} from 'src/utils/addProductModalHelpers'

const { Option } = Select

const buildStockTakeProductKey = product => {
  const supplier = String(product?.supplierTitle || '')
    .trim()
    .toLowerCase()
  const sku = String(product?.sku || '')
    .trim()
    .toLowerCase()
  const title = String(product?.title || '')
    .trim()
    .toLowerCase()
  if (sku) return `sku::${supplier}::${sku}`
  if (title) return `title::${supplier}::${title}`
  return ''
}

const AddProductModal = ({
  product,
  setProduct,
  venueProducts = [],
  recipes = [],
  addProductModalVisible,
  stockTakeProducts = [],
  onSubmit,
  addProductResetCount = 0,
  isSubmitDisabled = false,
}) => {
  const [mode, setMode] = useState('oms')
  const [title, setTitle] = useState('')
  const [sku, setSku] = useState('')
  const [size, setSize] = useState('')
  const [UoM, setUoM] = useState('')
  const [supplierTitle, setSupplierTitle] = useState('')
  const [price, setPrice] = useState(0)
  const [lastQuantity, setLastQuantity] = useState(0)
  const [inStock, setInStock] = useState(0)
  const [wastage, setWastage] = useState(0)
  const [inputValue, setInputValue] = useState('')
  const [selectedVenueProductId, setSelectedVenueProductId] = useState('')
  const [recipeSearchQuery, setRecipeSearchQuery] = useState('')
  const [selectedRecipeId, setSelectedRecipeId] = useState('')
  const [recipePriceMode, setRecipePriceMode] = useState('Portion')
  const refToInputTitle = useRef(null)
  const refToOmsSearch = useRef(null)
  const refToRecipeSearch = useRef(null)
  const lastAddProductResetCount = useRef(0)

  const validationOnNumber = (value, setValue) => {
    if (isNaN(value)) {
      message.error('Data in this field must be a numeric')
    } else setValue(value)
  }

  const existingStockTakeProductKeys = useMemo(() => {
    const keys = new Set()
    ;(stockTakeProducts || []).forEach(stockTakeProduct => {
      const key = buildStockTakeProductKey(stockTakeProduct)
      if (key) keys.add(key)
    })
    return keys
  }, [stockTakeProducts])

  const existingRecipeIds = useMemo(() => {
    const ids = new Set()
    ;(stockTakeProducts || []).forEach(stockTakeProduct => {
      const recipeId = getRecipeIdString(stockTakeProduct?.recipeId)
      if (recipeId) ids.add(recipeId)
    })
    return ids
  }, [stockTakeProducts])

  const isProductAlreadyInStockTake = candidate => {
    const key = buildStockTakeProductKey(candidate)
    return Boolean(key && existingStockTakeProductKeys.has(key))
  }

  const mappedVenueProducts = useMemo(() => {
    return (venueProducts || []).map(item => ({
      venueProductId: item?._id,
      title: item?.product?.title || item?.title || '',
      sku: item?.product?.sku || item?.sku || '',
      supplierTitle: item?.supplierTitle || item?.supplier?.title || '',
      size: item?.product?.size || item?.size || '',
      UoM: item?.product?.UoM || item?.UoM || '',
      hasVat: Boolean(item?.product?.hasVat || item?.hasVat),
      price:
        Number(item?.product?.price) > 0
          ? Number(item.product.price)
          : Number(item?.product?.OMSPrice) > 0
          ? Number(item.product.OMSPrice)
          : Number(item?.price || 0),
      isAlreadyInStockTake: isProductAlreadyInStockTake({
        title: item?.product?.title || item?.title || '',
        supplierTitle: item?.supplierTitle || item?.supplier?.title || '',
        sku: item?.product?.sku || item?.sku || '',
      }),
    }))
  }, [existingStockTakeProductKeys, venueProducts])

  const filteredOmsProducts = useMemo(() => {
    const query = String(inputValue || '')
      .trim()
      .toLowerCase()
    if (!query) return mappedVenueProducts
    return mappedVenueProducts.filter(item => {
      const itemTitle = String(item.title || '').toLowerCase()
      const itemSku = String(item.sku || '').toLowerCase()
      const supplier = String(item.supplierTitle || '').toLowerCase()
      return (
        itemTitle.includes(query) ||
        itemSku.includes(query) ||
        supplier.includes(query)
      )
    })
  }, [inputValue, mappedVenueProducts])

  const selectedOmsProduct = useMemo(
    () =>
      mappedVenueProducts.find(
        item => item.venueProductId === selectedVenueProductId,
      ) || null,
    [mappedVenueProducts, selectedVenueProductId],
  )

  const mappedRecipes = useMemo(() => {
    return (recipes || []).map(recipe => {
      const recipeId = getRecipeIdString(recipe)
      const portions = Number(recipe?.portions) || 1
      const costPerPortion = Number(recipe?.costPerPortion) || 0
      const costPerRecipe =
        Number(recipe?.costPerRecipe) > 0
          ? Number(recipe.costPerRecipe)
          : costPerPortion * portions
      return {
        recipeId,
        title: recipe?.title || 'Untitled recipe',
        portions,
        costPerPortion,
        costPerRecipe,
        hasVat: Boolean(recipe?.hasVat),
        isAlreadyInStockTake: existingRecipeIds.has(recipeId),
      }
    })
  }, [existingRecipeIds, recipes])

  const filteredRecipes = useMemo(() => {
    const query = String(recipeSearchQuery || '')
      .trim()
      .toLowerCase()
    if (!query) return mappedRecipes
    return mappedRecipes.filter(recipe =>
      String(recipe.title || '')
        .toLowerCase()
        .includes(query),
    )
  }, [mappedRecipes, recipeSearchQuery])

  const selectedRecipe = useMemo(
    () =>
      mappedRecipes.find(recipe => recipe.recipeId === selectedRecipeId) ||
      null,
    [mappedRecipes, selectedRecipeId],
  )

  const selectedRecipeSource = useMemo(
    () =>
      (recipes || []).find(
        recipe => getRecipeIdString(recipe) === selectedRecipeId,
      ) || null,
    [recipes, selectedRecipeId],
  )

  useEffect(() => {
    if (!addProductModalVisible) return
    resetFormOnOpen()
  }, [addProductModalVisible])

  useEffect(() => {
    if (addProductResetCount <= lastAddProductResetCount.current) return
    lastAddProductResetCount.current = addProductResetCount
    resetAfterAdd()
    window.requestAnimationFrame(() => focusFirstField())
  }, [addProductResetCount, mode])

  useEffect(() => {
    if (mode !== 'recipe' || !selectedRecipeSource) return
    const nextPrice = getRecipePriceForMode(
      selectedRecipeSource,
      recipePriceMode,
    )
    setPrice(nextPrice)
    setParentProduct({
      price: nextPrice,
      OMSPrice: nextPrice,
      recipePriceMode,
    })
  }, [mode, recipePriceMode, selectedRecipeId, selectedRecipeSource])

  const focusFirstField = () => {
    const focusField = getAddProductFocusField(mode)
    if (focusField === ADD_PRODUCT_FOCUS_FIELD.oms) {
      refToOmsSearch.current?.focus()
      return
    }
    if (focusField === ADD_PRODUCT_FOCUS_FIELD.recipe) {
      refToRecipeSearch.current?.focus()
      return
    }
    refToInputTitle.current?.focus()
  }

  const resetFormFields = currentMode => {
    setTitle('')
    setSku('')
    setPrice(0)
    setUoM('')
    setSupplierTitle(currentMode === 'recipe' ? RECIPE_SUPPLIER_TITLE : '')
    setSize(currentMode === 'recipe' ? '1' : '')
    setLastQuantity(0)
    setInStock(0)
    setWastage(0)
    setInputValue('')
    setSelectedVenueProductId('')
    setRecipeSearchQuery('')
    setSelectedRecipeId('')
    setProduct({
      ...product,
      ...buildEmptyAddProductFields(currentMode, recipePriceMode),
    })
  }

  function resetFormOnOpen() {
    setMode('oms')
    setRecipePriceMode('Portion')
    resetFormFields('oms')
  }

  function resetAfterAdd() {
    resetFormFields(mode)
  }

  const handleSubmitFromEnter = event => {
    event?.preventDefault?.()
    if (isSubmitDisabled || typeof onSubmit !== 'function') return
    onSubmit()
  }

  const setParentProduct = nextFields => {
    setProduct({
      ...product,
      ...nextFields,
    })
  }

  const resetRecipeProductFields = () => ({
    type: '',
    recipeId: null,
    recipePortionsPerBatch: null,
    recipePriceMode: 'Portion',
  })

  const handleOnSelectOmsProduct = item => {
    if (!item || item.isAlreadyInStockTake) return
    setSelectedVenueProductId(item.venueProductId)
    setTitle(item.title)
    setSku(item.sku || '')
    setPrice(item.price)
    setUoM(item.UoM)
    setSupplierTitle(item.supplierTitle)
    setSize(item.size)
    setParentProduct({
      ...resetRecipeProductFields(),
      title: item.title,
      sku: item.sku || '',
      price: item.price,
      OMSPrice: item.price,
      supplierTitle: item.supplierTitle,
      size: item.size,
      UoM: item.UoM,
      hasVat: item.hasVat,
      inStock,
      wastage,
      lastQuantity,
    })
  }

  const handleOnSelectRecipe = recipeItem => {
    if (!recipeItem || recipeItem.isAlreadyInStockTake) return
    const recipe =
      (recipes || []).find(
        item => getRecipeIdString(item) === recipeItem.recipeId,
      ) || null
    if (!recipe) return

    const portions = Number(recipe.portions) || 1
    const nextPrice = getRecipePriceForMode(recipe, recipePriceMode)
    const nextSku = buildDefaultRecipeSku(recipe)

    setSelectedRecipeId(recipeItem.recipeId)
    setSelectedVenueProductId('')
    setTitle(recipe.title || '')
    setSku(nextSku)
    setSize('1')
    setUoM('Portion')
    setSupplierTitle(RECIPE_SUPPLIER_TITLE)
    setPrice(nextPrice)
    setParentProduct({
      title: recipe.title || '',
      type: RECIPE_TYPE,
      sku: nextSku,
      size: '1',
      UoM: 'Portion',
      supplierTitle: RECIPE_SUPPLIER_TITLE,
      price: nextPrice,
      OMSPrice: nextPrice,
      hasVat: Boolean(recipe.hasVat),
      recipeId: recipeItem.recipeId,
      recipePortionsPerBatch: portions,
      recipePriceMode,
      inStock,
      wastage,
      lastQuantity,
    })
  }

  const switchMode = nextMode => {
    setMode(nextMode)
    setSelectedVenueProductId('')
    setSelectedRecipeId('')
    setRecipeSearchQuery('')
    setRecipePriceMode('Portion')

    if (nextMode === 'custom') {
      setTitle('')
      setSku('')
      setPrice(0)
      setSupplierTitle('')
      setSize('')
      setUoM('')
      setParentProduct({
        ...resetRecipeProductFields(),
        title: '',
        sku: '',
        price: 0,
        OMSPrice: 0,
        supplierTitle: '',
        size: '',
        UoM: '',
      })
      refToInputTitle.current?.focus()
      return
    }

    if (nextMode === 'oms') {
      setTitle('')
      setSku('')
      setPrice(0)
      setSupplierTitle('')
      setSize('')
      setUoM('')
      setParentProduct({
        ...resetRecipeProductFields(),
        title: '',
        sku: '',
        price: 0,
        OMSPrice: 0,
        supplierTitle: '',
        size: '',
        UoM: '',
      })
      return
    }

    if (nextMode === 'recipe') {
      setSupplierTitle(RECIPE_SUPPLIER_TITLE)
      setSize('1')
      setUoM('Portion')
      setTitle('')
      setSku('')
      setPrice(0)
      setParentProduct({
        ...resetRecipeProductFields(),
        type: RECIPE_TYPE,
        supplierTitle: RECIPE_SUPPLIER_TITLE,
        size: '1',
        UoM: 'Portion',
        title: '',
        sku: '',
        price: 0,
        OMSPrice: 0,
      })
    }
  }

  const isRecipeMode = mode === 'recipe'
  const priceAddonLabel = isRecipeMode
    ? `Price (${
        recipePriceMode === 'Full Recipe' ? 'per recipe' : 'per portion'
      }): `
    : 'Price: '
  const inStockAddonLabel = isRecipeMode
    ? 'In stock (portions): '
    : 'In stock: '
  const wastageAddonLabel = isRecipeMode ? 'Wastage (portions): ' : 'Wastage: '

  return (
    <form
      onSubmit={event => {
        event.preventDefault()
        handleSubmitFromEnter(event)
      }}
    >
      <ModeTabs>
        <ModeTab active={mode === 'oms'} onClick={() => switchMode('oms')}>
          From OMS
        </ModeTab>
        <ModeTab
          active={mode === 'custom'}
          onClick={() => switchMode('custom')}
        >
          Custom
        </ModeTab>
        <ModeTab
          active={mode === 'recipe'}
          onClick={() => switchMode('recipe')}
        >
          Recipe
        </ModeTab>
      </ModeTabs>

      {mode === 'oms' && (
        <>
          <StyledDiv>
            <Input
              ref={refToOmsSearch}
              value={inputValue}
              placeholder='Search products by name, SKU, or supplier'
              onChange={e => setInputValue(e.target.value)}
            />
          </StyledDiv>
          <HintText>Search includes title, SKU and supplier.</HintText>
          <HintText>
            Most products appear automatically from OMS orders. Use this for
            missing items only.
          </HintText>
          {selectedOmsProduct && (
            <SelectedSummary>
              Selected: {selectedOmsProduct.title} •{' '}
              {selectedOmsProduct.supplierTitle || 'Unknown supplier'} • SKU:{' '}
              {selectedOmsProduct.sku || '-'} • £
              {Number(selectedOmsProduct.price || 0).toFixed(2)}
            </SelectedSummary>
          )}
          <OptionsList>
            {filteredOmsProducts.map(item => (
              <OptionRow
                key={item.venueProductId}
                disabled={item.isAlreadyInStockTake}
                selected={selectedVenueProductId === item.venueProductId}
                onClick={() => handleOnSelectOmsProduct(item)}
              >
                <OptionTitle>{item.title || 'Untitled product'}</OptionTitle>
                <OptionMeta>
                  {item.supplierTitle || 'Unknown supplier'} | SKU:{' '}
                  {item.sku || '-'} | £{Number(item.price || 0).toFixed(2)}
                </OptionMeta>
                {item.isAlreadyInStockTake && (
                  <OptionTag>Already in this stock take</OptionTag>
                )}
              </OptionRow>
            ))}
          </OptionsList>
          <HintText>Greyed items are already in this stock take.</HintText>
        </>
      )}

      {mode === 'custom' && (
        <HintText>Use Custom for non-OMS items only.</HintText>
      )}

      {mode === 'recipe' && (
        <>
          <RecipeExplainer>
            <strong>Count finished dishes you have already made.</strong> For
            example, a chocolate cake recipe might make 12 portions. If you have
            10 portions left in the fridge, add the recipe here and enter{' '}
            <strong>10</strong> in <strong>In stock (portions)</strong>.
          </RecipeExplainer>
          <HintText>
            This counts the finished dish, not individual ingredients. To record
            what was sold and update ingredient usage, use{' '}
            <strong>More → Add Sold Recipes</strong>.
          </HintText>
          <StyledDiv>
            <Input
              ref={refToRecipeSearch}
              value={recipeSearchQuery}
              placeholder='Search recipes by name'
              onChange={e => setRecipeSearchQuery(e.target.value)}
            />
          </StyledDiv>
          {selectedRecipe && (
            <SelectedSummary>
              Selected: {selectedRecipe.title} • {selectedRecipe.portions}{' '}
              portions per recipe • £
              {Number(
                getRecipePriceForMode(selectedRecipeSource, recipePriceMode),
              ).toFixed(2)}{' '}
              (
              {recipePriceMode === 'Full Recipe' ? 'per recipe' : 'per portion'}
              )
            </SelectedSummary>
          )}
          <OptionsList>
            {filteredRecipes.length === 0 ? (
              <EmptyRecipeRow>
                {recipeSearchQuery.trim()
                  ? 'No recipes match your search.'
                  : 'No recipes available. Create recipes in Menu Builder first.'}
              </EmptyRecipeRow>
            ) : (
              filteredRecipes.map(item => (
                <OptionRow
                  key={item.recipeId}
                  disabled={item.isAlreadyInStockTake}
                  selected={selectedRecipeId === item.recipeId}
                  onClick={() => handleOnSelectRecipe(item)}
                >
                  <OptionTitle>{item.title}</OptionTitle>
                  <OptionMeta>
                    {item.portions} portions | £
                    {Number(item.costPerPortion || 0).toFixed(2)} per portion |
                    £{Number(item.costPerRecipe || 0).toFixed(2)} per recipe
                  </OptionMeta>
                  {item.isAlreadyInStockTake && (
                    <OptionTag>Already in this stock take</OptionTag>
                  )}
                </OptionRow>
              ))
            )}
          </OptionsList>
          <HintText>Greyed recipes are already in this stock take.</HintText>
          <StyledDiv>
            <FieldLabel>Price based on:</FieldLabel>
            <Select
              value={recipePriceMode}
              style={{ flex: 1 }}
              onChange={value => {
                setRecipePriceMode(value)
                setParentProduct({ recipePriceMode: value })
              }}
            >
              <Option value='Portion'>Cost per portion</Option>
              <Option value='Full Recipe'>Cost per full recipe</Option>
            </Select>
          </StyledDiv>
          <HintText>
            In stock and wastage are always counted in portions. Stock value
            uses your selected price basis.
          </HintText>
        </>
      )}

      <StyledDiv>
        <Input
          ref={refToInputTitle}
          value={title}
          placeholder='Enter title of product'
          addonBefore='Title: '
          onChange={e => {
            setTitle(e.target.value)
            setParentProduct({ title: e.target.value })
          }}
          onFocus={e => e.target.select()}
        />
      </StyledDiv>
      <StyledDiv>
        <Input
          value={sku}
          placeholder='Enter SKU (optional)'
          addonBefore='SKU: '
          onChange={e => {
            setSku(e.target.value)
            setParentProduct({ sku: e.target.value })
          }}
          onFocus={e => e.target.select()}
        />
      </StyledDiv>
      <StyledDiv>
        <Input
          value={size}
          placeholder='Enter size of product'
          addonBefore='Size: '
          disabled={isRecipeMode}
          onChange={e => {
            setSize(e.target.value)
            setParentProduct({ size: e.target.value })
          }}
          onFocus={e => e.target.select()}
          required={true}
        />
      </StyledDiv>
      <StyledDiv>
        <Input
          value={UoM}
          placeholder='Enter UoM'
          addonBefore='UoM: '
          disabled={isRecipeMode}
          onChange={e => {
            setUoM(e.target.value)
            setParentProduct({ UoM: e.target.value })
          }}
          onFocus={e => e.target.select()}
        />
      </StyledDiv>
      <StyledDiv>
        <Input
          value={supplierTitle}
          placeholder='Enter title of supplier'
          addonBefore='Supplier: '
          disabled={isRecipeMode}
          onChange={e => {
            setSupplierTitle(e.target.value)
            setParentProduct({ supplierTitle: e.target.value })
          }}
          onFocus={e => e.target.select()}
        />
      </StyledDiv>
      <StyledDiv>
        <Input
          value={price}
          placeholder='Enter price of product'
          addonBefore={priceAddonLabel}
          onChange={e =>
            validationOnNumber(e.target.value, value => {
              setPrice(value)
              setParentProduct({ price: value, OMSPrice: value })
            })
          }
          onFocus={e => e.target.select()}
        />
      </StyledDiv>
      <StyledDiv>
        <Input
          value={lastQuantity}
          placeholder='Last quantity'
          addonBefore={
            isRecipeMode ? 'Last quantity (portions): ' : 'Last quantity: '
          }
          onChange={e =>
            validationOnNumber(e.target.value, value => {
              setLastQuantity(value)
              setParentProduct({ lastQuantity: value })
            })
          }
          onFocus={e => e.target.select()}
        />
      </StyledDiv>
      <StyledDiv>
        <Input
          value={inStock}
          placeholder='Enter current quantity'
          addonBefore={inStockAddonLabel}
          onChange={e =>
            validationOnNumber(e.target.value, value => {
              setInStock(value)
              setParentProduct({ inStock: value })
            })
          }
          onFocus={e => e.target.select()}
          onPressEnter={handleSubmitFromEnter}
        />
      </StyledDiv>
      <StyledDiv>
        <Input
          value={wastage}
          placeholder='Enter current wastage count'
          addonBefore={wastageAddonLabel}
          onChange={e =>
            validationOnNumber(e.target.value, value => {
              setWastage(value)
              setParentProduct({ wastage: value })
            })
          }
          onFocus={e => e.target.select()}
          onPressEnter={handleSubmitFromEnter}
        />
      </StyledDiv>
      <StyledDiv>
        <StyledSpan>Has VAT ?</StyledSpan>
        <Checkbox
          checked={product.hasVat}
          onChange={() => setParentProduct({ hasVat: !product.hasVat })}
        />
      </StyledDiv>
    </form>
  )
}

const ModeTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`

const ModeTab = styled.button`
  border: none;
  border-radius: 8px;
  background-color: ${({ active, theme }) =>
    active ? theme.colours.lettuceGreen : '#f0f0f0'};
  color: ${({ active, theme }) =>
    active ? theme.colours.mayoWhite : theme.colours.bbqBlack};
  font-weight: 700;
  padding: 8px 18px;
  cursor: pointer;
`

const HintText = styled.p`
  color: #6f6f6f;
  font-size: 12px;
  margin-bottom: 10px;
`

const RecipeExplainer = styled.p`
  color: #434343;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
`

const SelectedSummary = styled.p`
  color: #111;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 10px;
`

const OptionsList = styled.div`
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #efefef;
  border-radius: 8px;
  margin-bottom: 10px;
`

const EmptyRecipeRow = styled.div`
  padding: 12px;
  color: #6f6f6f;
  font-size: 12px;
`

const OptionRow = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  border-bottom: 1px solid #f2f2f2;
  padding: 10px 12px;
  background: ${({ selected, disabled }) =>
    disabled ? '#f5f5f5' : selected ? '#f6ffed' : '#fff'};
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`

const OptionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
`

const OptionMeta = styled.div`
  margin-top: 2px;
  color: #6f6f6f;
  font-size: 12px;
`

const OptionTag = styled.div`
  margin-top: 4px;
  color: #2f54eb;
  font-size: 11px;
  font-weight: 700;
`

const StyledDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
`

const FieldLabel = styled.span`
  min-width: 110px;
  margin-right: 8px;
  color: #595959;
  font-weight: 600;
`

const StyledSpan = styled.span`
  margin-right: 7px;
`

export default AddProductModal
